import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import {
  getBusinessUsers,
  getCorePlan,
  getTracksQuota,
  getUsersQuota,
} from '@disco/state-management/selectors/user'
import { getDefaultPlanQuotas } from '@disco/utils/pricing'
import type { BusinessStats } from 'types/business'

const usePreviewAllocateComponent = ({ options }) => {
  const [previewLoaded, setPreviewLoaded] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )

  const loadPreveiw = () => {
    if (
      billingProviderComponents.loading ||
      !billingProviderComponents.loaded ||
      previewLoading
    ) {
      return
    }

    setPreviewLoading(false)
    setPreviewLoaded(true)
  }

  useEffect(loadPreveiw, [billingProviderComponents])
  return {
    options,
    loaded: previewLoaded,
    loading: previewLoading,
    loadPreveiw,
  }
}

export const useDynamicUserOptions = (
  customUserAllowance?: number | null | undefined,
  customUserkCount?: number | null | undefined,
): {
  loadPreveiw: () => void
  loaded: boolean
  loading: boolean
  options: Array<any>
} => {
  const usersQuota = useSelector(getUsersQuota)
  const { number_of_users } = useSelector(
    state => state.user.business?.stats || ({} as BusinessStats),
  )
  const businessUsers = useSelector(getBusinessUsers)
  const corePlan = useSelector(getCorePlan)
  let userAllowance = customUserAllowance || usersQuota

  if (typeof customUserAllowance === 'number') {
    userAllowance = customUserAllowance
  }

  let numberOfUsers =
    customUserkCount || businessUsers?.users?.length || number_of_users || 0

  if (typeof customUserkCount === 'number') {
    numberOfUsers = customUserkCount
  }

  const options = useMemo(() => {
    const isDefaultCountOptions = userAllowance <= 5
    const defaultPlanQuotas = getDefaultPlanQuotas(corePlan?.id)
    let startCount = Math.max(defaultPlanQuotas?.users, userAllowance - 5)

    const computedOptions = new Array<number>(isDefaultCountOptions ? 10 : 11)
      .fill(0)
      .map<Record<string, any>>(() => {
        const value = startCount
        startCount++
        return {
          value: value - defaultPlanQuotas?.users,
          label: `${value} seats`,
          disabled: value < numberOfUsers || value == userAllowance,
        }
      })

    return computedOptions
  }, [corePlan?.id, numberOfUsers, userAllowance])

  return usePreviewAllocateComponent({
    options,
  })
}
export const useDynamicTracksOptions = (
  customConfig?:
    | {
        customTrackAllowance: number | null | undefined
        customTrackCount: number | null | undefined
        customLimits:
          | (number | { value: number; label: string })[]
          | null
          | undefined
        customType?: string
        customDisabledCondition?: (...args: Array<any>) => any
        quotaIsNotScalable?: boolean
      }
    | null
    | undefined,
): {
  loadPreveiw: () => void
  loaded: boolean
  loading: boolean
  options: Array<any>
} => {
  const {
    customTrackAllowance,
    customTrackCount,
    customLimits,
    customType,
    customDisabledCondition,
    quotaIsNotScalable,
  } = customConfig || {}
  const tracksQuota = useSelector(getTracksQuota)
  const { total_track_count } = useSelector(
    state => state.user.business?.stats || ({} as BusinessStats),
  )
  const corePlan = useSelector(getCorePlan)
  let trackAllowance = tracksQuota

  if (typeof customTrackAllowance === 'number') {
    trackAllowance = customTrackAllowance
  }

  let trackCount = customTrackCount || total_track_count || 0

  if (typeof customTrackCount === 'number') {
    trackCount = customTrackCount
  }

  const defaultPlanQuotas = getDefaultPlanQuotas(corePlan?.id)

  const options = useMemo(() => {
    const tracksLimits = (() => {
      const list = customLimits || [1000]
      if (quotaIsNotScalable) return list
      if (trackAllowance === Infinity) return [] // unlimited artist tracks come with quota=Infinity
      let start = true
      let steps = 10

      while (start) {
        const lastItem = list[list.length - 1]
        const last = typeof lastItem === 'number' ? lastItem : lastItem.value

        if (steps === 0) {
          start = false
          break
        }
        if (list.length > 999) {
          // safeguard from infinite loop
          break
        }

        if (last >= trackAllowance) {
          steps--
        }

        if (last < 10000) {
          list.push(last + 1000)
        } else {
          list.push(last + 5000)
        }
      }

      const currentAllowanceIndex = list.findIndex(v => v == trackAllowance)
      return list
        .slice(
          Math.max(currentAllowanceIndex - 5, 0),
          currentAllowanceIndex + 10,
        )
        .splice(0, 11)
    })()

    const computedOptions = tracksLimits.map(item => {
      let value, label

      if (typeof item === 'object') {
        value = item.value
        label = item.label
      } else {
        value = item
        label = `${value} tracks`
      }

      return {
        // Adjust value only for tracks, not for DS or WM
        value: customType ? value : value - defaultPlanQuotas?.files,
        label,
        disabled:
          value < trackCount ||
          value == trackAllowance ||
          (customDisabledCondition && customDisabledCondition(value)),
      }
    })
    return computedOptions
  }, [
    customDisabledCondition,
    customType,
    defaultPlanQuotas?.files,
    trackAllowance,
    trackCount,
    customLimits,
    quotaIsNotScalable,
  ])
  return usePreviewAllocateComponent({
    options,
  })
}
